import { useState } from "react";
import { Link, useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { MailCheck } from "lucide-react";
import { Button } from "@pracownik/ui";
import { sendVerificationEmail } from "@/lib/auth-client";
import { AuthLayout } from "@/layouts/auth-layout";

export function VerifyEmailSentPage() {
  const { t } = useTranslation("auth");
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email") ?? "";
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(false);

  const handleResend = async () => {
    if (!email) {
      toast.error(t("verifyEmailSent.missingEmail"));
      return;
    }
    setResending(true);
    try {
      const result = await sendVerificationEmail({
        email,
        callbackURL: `${window.location.origin}/dashboard`,
      });
      if (result.error) {
        toast.error(t("verifyEmailSent.resendFailed"));
        return;
      }
      toast.success(t("verifyEmailSent.resent"));
      setCooldown(true);
      setTimeout(() => setCooldown(false), 30_000);
    } catch {
      toast.error(t("verifyEmailSent.resendFailed"));
    } finally {
      setResending(false);
    }
  };

  return (
    <AuthLayout
      title={t("verifyEmailSent.title")}
      description={
        email
          ? t("verifyEmailSent.descriptionWithEmail", { email })
          : t("verifyEmailSent.description")
      }
      icon={<MailCheck />}
    >
      <ol className="mb-6 space-y-2 text-sm text-text-secondary">
        {[t("verifyEmailSent.step1"), t("verifyEmailSent.step2"), t("verifyEmailSent.step3")].map(
          (step, i) => (
            <li key={i} className="flex gap-3">
              <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-primary-soft text-[11px] font-semibold text-primary">
                {i + 1}
              </span>
              <span>{step}</span>
            </li>
          ),
        )}
      </ol>
      <p className="mb-4 text-center text-xs text-text-tertiary">
        {t("verifyEmailSent.checkSpam")}
      </p>
      <Button
        variant="outline"
        size="lg"
        className="w-full"
        onClick={handleResend}
        loading={resending}
        disabled={!email || cooldown}
      >
        {cooldown ? t("verifyEmailSent.resentShort") : t("verifyEmailSent.resend")}
      </Button>
      <p className="mt-6 text-center text-sm">
        <Link to="/login" className="font-medium text-primary hover:underline">
          {t("verifyEmailSent.backToLogin")}
        </Link>
      </p>
    </AuthLayout>
  );
}
